import { useState, useEffect, useRef } from "react";
import { Box, Tooltip } from "@mui/material";
import {
  motion,
  AnimatePresence,
} from "framer-motion";
import { FaArrowUp } from "react-icons/fa";

const EASE = [0.16, 1, 0.3, 1];
const SHOW_AT = 400; // px scrolled before button appears

export default function BackToTop() {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [clicking, setClicking] = useState(false);
  const progressRef = useRef(null);

  // ── Track scroll position + circular progress ──────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      const scrolled = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? scrolled / docHeight : 0;

      setVisible(scrolled > SHOW_AT);

      // Update SVG circle stroke-dashoffset
      if (progressRef.current) {
        const circumference = 2 * Math.PI * 20; // r=20
        progressRef.current.style.strokeDashoffset =
          circumference - progress * circumference;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // run once on mount
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => {
    setClicking(true);
    setTimeout(() => setClicking(false), 600);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const circumference = 2 * Math.PI * 20;

  return (
    <AnimatePresence>
      {visible && (
        <Tooltip title="Back to top" placement="left" arrow>
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 24, filter: "blur(8px)" }}
            animate={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            exit={{ opacity: 0, scale: 0.5, y: 24, filter: "blur(8px)" }}
            transition={{ duration: 0.55, ease: EASE }}
            style={{
              position: "fixed",
              bottom: 32,
              right: 28,
              zIndex: 9999,
              cursor: "pointer",
            }}
            onClick={scrollToTop}
            onHoverStart={() => setHovered(true)}
            onHoverEnd={() => setHovered(false)}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.92 }}
          >
            <Box sx={{ position: "relative", width: 52, height: 52 }}>
              {/* ── Glow pulse ring (shows on hover) ───────────────────── */}
              <AnimatePresence>
                {hovered && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.3 }}
                    transition={{ duration: 0.4, ease: EASE }}
                    style={{
                      position: "absolute",
                      inset: -6,
                      borderRadius: "50%",
                      background: "rgba(2,136,209,0.15)",
                      border: "1px solid rgba(2,136,209,0.25)",
                      pointerEvents: "none",
                    }}
                  />
                )}
              </AnimatePresence>

              {/* ── SVG circular progress track ────────────────────────── */}
              <svg
                width="52"
                height="52"
                viewBox="0 0 52 52"
                style={{
                  position: "absolute",
                  inset: 0,
                  transform: "rotate(-90deg)",
                }}
              >
                {/* Track */}
                <circle
                  cx="26"
                  cy="26"
                  r="20"
                  fill="none"
                  stroke="rgba(2,136,209,0.15)"
                  strokeWidth="2.5"
                />
                {/* Progress */}
                <circle
                  ref={progressRef}
                  cx="26"
                  cy="26"
                  r="20"
                  fill="none"
                  stroke="url(#progressGrad)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={circumference}
                  style={{ transition: "stroke-dashoffset 0.15s linear" }}
                />
                <defs>
                  <linearGradient
                    id="progressGrad"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    <stop offset="0%" stopColor="#0288D1" />
                    <stop offset="100%" stopColor="#7c3aed" />
                  </linearGradient>
                </defs>
              </svg>

              {/* ── Button face ────────────────────────────────────────── */}
              <Box
                sx={{
                  position: "absolute",
                  inset: 5,
                  borderRadius: "50%",
                  background: hovered
                    ? "linear-gradient(135deg, #0288D1, #0ea5e9)"
                    : "rgba(255,255,255,0.85)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid rgba(2,136,209,0.2)",
                  boxShadow: hovered
                    ? "0 8px 28px rgba(2,136,209,0.45)"
                    : "0 4px 16px rgba(2,136,209,0.18), 0 1px 4px rgba(0,0,0,0.08)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.3s cubic-bezier(0.16,1,0.3,1)",
                }}
              >
                <motion.div
                  animate={
                    clicking
                      ? { y: [-2, -7, 0], opacity: [1, 0.6, 1] }
                      : hovered
                      ? { y: -2 }
                      : { y: 0 }
                  }
                  transition={{ duration: 0.5, ease: EASE }}
                >
                  <FaArrowUp
                    size={15}
                    color={hovered ? "#fff" : "#0288D1"}
                    style={{ transition: "color 0.3s" }}
                  />
                </motion.div>
              </Box>
            </Box>
          </motion.div>
        </Tooltip>
      )}
    </AnimatePresence>
  );
}
